// Custom dropdown implementation with reference from
// https://react-bootstrap.github.io/components/dropdowns/#custom-dropdown-components
import { AnimatePresence, motion } from "framer-motion";
import {
  CSSProperties,
  Children,
  MouseEventHandler,
  PropsWithChildren,
  forwardRef,
  useId,
  useMemo,
} from "react";
import { Dropdown as BootstrapDropdown } from "react-bootstrap";
import { DropdownMenuProps } from "react-bootstrap/esm/DropdownMenu";
import { DropDirection } from "react-bootstrap/esm/DropdownContext";
import {
  ChevronWrapper,
  DropdownButton,
  DropdownHeaderContainer,
  DropdownItemButton,
  DropdownMenuWrapper,
} from "./style";
import { Chevron } from "../Chevron/style";

const props = {
  transition: {
    duration: 0.25,
    ease: "easeInOut",
  },
  initial: {
    opacity: 0,
    translateY: 8,
  },
  animate: {
    opacity: 1,
    translateY: 0,
  },
  exit: {
    opacity: 0,
    translateY: 8,
  },
};

// THE TOGGLE
//
// `...rest` is not decoration. react-bootstrap hands its toggle the entire
// accessibility contract -- aria-expanded, aria-haspopup, the id the menu is
// labelled by, and the key handlers that open it -- and this component used to
// destructure `onClick` and `style` and drop everything else on the floor. The
// result was a bare <button> with no role and no keyboard: arrow keys fell
// through to the global shortcut manager, so pressing Right while the perps
// size-unit control was focused changed the ORDER TYPE instead of the unit.
interface IDropdownToggleProps {
  onClick: () => void;
  style: CSSProperties;
  /** Injected by react-bootstrap; deliberately not forwarded (see below). */
  className?: string;
}
const makeDropdownToggle = (
  role?: "combobox",
  label?: string,
  menuId?: string
) =>
  forwardRef<any, PropsWithChildren<IDropdownToggleProps>>(
    ({ children, onClick, style, className: _bsClass, ...rest }, ref) => (
      // `className` is dropped on purpose. react-bootstrap computes
      // `classNames(className, 'dropdown-toggle', show && 'show')`, and
      // src/style.tsx imports bootstrap.min.css globally -- so forwarding it
      // gives every toggle in the app a Bootstrap `::after` caret on top of
      // the design system's own chevron. The ARIA and the handlers in `...rest`
      // are what we actually want from the library.
      <DropdownButton
        ref={ref}
        onClick={onClick}
        style={style}
        role={role}
        // A button takes its accessible name from its own text; a combobox
        // does NOT -- it needs an explicit label, or it announces as unnamed.
        // Changing the role without this would trade one defect for a worse one.
        aria-label={role ? label : undefined}
        aria-haspopup={role ? "listbox" : undefined}
        // Only while the popup EXISTS. react-bootstrap does not mount the menu
        // until first open, and aria-controls pointing at a node that is not in
        // the DOM is itself a violation. `aria-expanded` arrives in `...rest`
        // from the library, so no extra state is needed to know.
        aria-controls={
          role && menuId && (rest as Record<string, unknown>)["aria-expanded"]
            ? menuId
            : undefined
        }
        {...rest}
      >
        {children}
      </DropdownButton>
    )
  );

const DefaultDropdownToggle = makeDropdownToggle();

// THE MENU
interface IDropdownMenuProps {
  style: CSSProperties;
  className: string;
  "aria-labelledby": string;
  hasAnnouncement: boolean;
}
const DropdownMenu = forwardRef<any, PropsWithChildren<IDropdownMenuProps>>(
  (
    {
      children,
      style,
      className,
      "aria-labelledby": labeledBy,
      hasAnnouncement,
      ...rest
    },
    ref
  ) => (
    <DropdownMenuWrapper
      ref={ref}
      style={style}
      className={className}
      aria-labelledby={labeledBy}
      hasAnnouncement={hasAnnouncement}
      {...rest}
    >
      {/* <ul className="list-unstyled"> */}
      {Children.toArray(children)}
      {/* </ul> */}
    </DropdownMenuWrapper>
  )
);

// THE ITEMS
interface IDropdownItemProps {
  active: boolean;
  onClick?: () => void;
  /** Injected by react-bootstrap; deliberately not forwarded (see below). */
  className?: string;
}
const DropdownItem = forwardRef<any, PropsWithChildren<IDropdownItemProps>>(
  // `className` dropped for the same reason as the toggle: it carries
  // `dropdown-item` and `active`, and Bootstrap's `.dropdown-item.active`
  // (specificity 0,2,0) outranks the styled-component class whatever the
  // injection order, painting the selected row Bootstrap blue on a dark theme.
  //
  // `active` is destructured out and not re-emitted: react-bootstrap consumes
  // it in useDropdownItem and never forwards the raw prop, so emitting it here
  // would be meaningless.
  //
  // The library DOES supply `aria-selected` (not `aria-current`, as this
  // comment used to claim -- verified in @restart/ui@1.9.4's useDropdownItem),
  // and it arrives through `...rest`. That is now correct ARIA, because the
  // items carry role="option" when the dropdown opted into combobox/listbox
  // semantics. Until they did, it was landing on a roleless button, where it is
  // invalid and ignored -- which, combined with dropping `className`, left the
  // six RFQ dropdowns that DO pass `active:` with no selected state at all,
  // visual or accessible.
  ({ children, onClick, active: _active, className: _bsClass, ...rest }, ref) => (
    <DropdownItemButton ref={ref} type="button" onClick={onClick} {...rest} {...props}>
      {children}
    </DropdownItemButton>
  )
);

export interface IDropdownItemsItem {
  label: string | JSX.Element;
  isSeparator?: boolean;
  active?: boolean;
  onSelect?: MouseEventHandler<HTMLElement>;
}

// THE COMPONENT
interface IDropdownProps {
  title: string | JSX.Element;
  items: IDropdownItemsItem[];
  width?: number | string;
  height?: number | string;
  toggleStyles?: CSSProperties;
  dropdownMenuProps?: DropdownMenuProps;
  dropdownMenuContainerStyles?: CSSProperties;
  dropdownHeader?: JSX.Element;
  dropdownFooter?: JSX.Element;
  dropDirection?: DropDirection;

  // Optional callback on dropdown open
  isToggled?: boolean;
  onToggle?: (open: boolean) => void;
  preventHideOnSelect?: boolean;
  showArrow?: boolean;
  /**
   * Declares the toggle as a select-like control rather than a menu button.
   *
   * It matters for the keyboard, not just for screen readers. A menu button
   * owns Up/Down only, so Left/Right fall through to the global shortcut
   * manager -- and on the trade form those are bound to "cycle order type",
   * which meant arrowing on the size-unit control silently switched Market to
   * Limit. A combobox owns all four, so the manager stands down
   * (`ownsArrowKeys` in keyboard/KeyboardManager.ts).
   *
   * Opt-in: the other callers of this component really are menus.
   */
  toggleRole?: "combobox";
  /** Required with `toggleRole`: the combobox's accessible name. */
  toggleLabel?: string;
}
const Dropdown = forwardRef<any, PropsWithChildren<IDropdownProps>>(
  (
    {
      title,
      items,
      width,
      height,
      dropdownHeader,
      dropdownFooter,
      dropDirection = "down",
      isToggled,
      onToggle,
      toggleStyles,
      dropdownMenuProps,
      dropdownMenuContainerStyles,
      preventHideOnSelect,
      showArrow = false,
      toggleRole,
      toggleLabel,
    },
    ref
  ) => {
    const hasAnnouncement = useMemo(() => {
      const key = process.env.REACT_APP_ANNOUNCEMENT_KEY;
      return Boolean(key && !localStorage.getItem(key));
    }, []);

    // A stable id so the combobox can point at the listbox it owns. useId's
    // ":r3:" form is a legal HTML id but awkward in CSS selectors.
    const menuId = `dropdown-menu-${useId().replace(/:/g, "")}`;

    // A stable identity per role, so the toggle is not remounted every render.
    const Toggle = useMemo(
      () =>
        toggleRole
          ? makeDropdownToggle(toggleRole, toggleLabel, menuId)
          : DefaultDropdownToggle,
      [toggleRole, toggleLabel, menuId]
    );

    return (
      <BootstrapDropdown
        drop={dropDirection}
        ref={ref}
        style={{
          width,
          height: height ?? "100%",
        }}
        onToggle={onToggle}
      >
        <BootstrapDropdown.Toggle
          as={Toggle}
          style={{
            width,
            overflow: "hidden",
            ...toggleStyles,
          }}
        >
          {title}
          {showArrow ? (
            <ChevronWrapper>
              <Chevron direction={isToggled ? "up" : "down"} />
            </ChevronWrapper>
          ) : null}
        </BootstrapDropdown.Toggle>

        <BootstrapDropdown.Menu
          as={DropdownMenu}
          // Opt-in, exactly like toggleRole. The other consumers are menus of
          // COMMANDS; giving them listbox semantics would be wrong. Without
          // this the toggle declared aria-haspopup="listbox" and opened a plain
          // <ul> of buttons -- a promise of a structure that does not exist,
          // which is worse for a screen reader than no role at all.
          id={toggleRole ? menuId : undefined}
          role={toggleRole ? "listbox" : undefined}
          aria-label={toggleRole ? toggleLabel : undefined}
          style={{
            width,
            ...dropdownMenuContainerStyles,
          }}
          {...dropdownMenuProps}
          hasAnnouncement={hasAnnouncement}
        >
          <DropdownHeaderContainer>{dropdownHeader}</DropdownHeaderContainer>
          <AnimatePresence>
            {items.map((item, i) =>
              item.isSeparator ? (
                <motion.div
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  {...props}
                >
                  {item.label}
                </motion.div>
              ) : (
                <BootstrapDropdown.Item
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  as={DropdownItem}
                  role={toggleRole ? "option" : undefined}
                  active={item.active}
                  onClick={(e) => {
                    if (preventHideOnSelect) {
                      e.stopPropagation();
                    }
                    item.onSelect?.(e);
                  }}
                >
                  {item.label}
                </BootstrapDropdown.Item>
              )
            )}
          </AnimatePresence>
          {dropdownFooter}
        </BootstrapDropdown.Menu>
      </BootstrapDropdown>
    );
  }
);

export default Dropdown;
